import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc, collection, addDoc, getDocs, query, where, orderBy, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import Header from '../components/Header';
import AOS from 'aos';
import 'aos/dist/aos.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import CategoriesWidget from '../components/CategoriesWidget';


const BlogDetail = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentForm, setCommentForm] = useState({
    name: '',
    email: '',
    website: '',
    comment: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const fetchPostAndComments = async () => {
      try {
        // Get the post
        const postDoc = await getDoc(doc(db, 'blogPosts', id));
        
        if (postDoc.exists()) {
          const postData = { id: postDoc.id, ...postDoc.data() };
          setPost(postData);
          
          // Get comments
          const commentsQuery = query(
            collection(db, 'comments'), 
            where('postId', '==', id),
            orderBy('createdAt', 'desc')
          );
          
          const commentsSnapshot = await getDocs(commentsQuery);
          const commentsData = commentsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          setComments(commentsData);
          
          // Get related posts (same category)
          if (postData.category) {
            const relatedQuery = query(
              collection(db, 'blogPosts'),
              where('category', '==', postData.category),
              where('id', '!=', id),
              orderBy('createdAt', 'desc')
            );
            
            const relatedSnapshot = await getDocs(relatedQuery);
            const relatedData = relatedSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            })).slice(0, 5); // Just take up to 5 related posts
            
            setRelatedPosts(relatedData);
          }
          
          setLoading(false);
        } else {
          setError('Post not found');
          setLoading(false);
        }
      } catch (error) {
        console.error("Error fetching blog post:", error);
        
        setLoading(false);
      }
    };

    if (id) {
      fetchPostAndComments();
    }
  }, [id]);



  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
  
    const image = new Image();
    image.src = '/assets/img/page-title-bg.webp'; // Preload the image
    image.onload = () => {
      const pageTitle = document.querySelector('.page-title');
      if (pageTitle) {
        pageTitle.style.backgroundImage = `url(${image.src})`;
      }
    };
  }, []);


  const handleCommentChange = (e) => {
    setCommentForm({
      ...commentForm,
      [e.target.name]: e.target.value
    });
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // Validate form
      if (!commentForm.name || !commentForm.email || !commentForm.comment) {
        setError('Please fill in all required fields');
        setSubmitting(false);
        return;
      }
      
      // Add comment to Firebase
      await addDoc(collection(db, 'comments'), {
        postId: id,
        name: commentForm.name,
        email: commentForm.email,
        website: commentForm.website,
        comment: commentForm.comment,
        createdAt: serverTimestamp()
      });
      
      // Reset form and show success message
      setCommentForm({
        name: '',
        email: '',
        website: '',
        comment: ''
      });
      setSuccess('Comment submitted successfully!');
      
      // Reload comments
      const commentsQuery = query(
        collection(db, 'comments'), 
        where('postId', '==', id),
        orderBy('createdAt', 'desc')
      );
      
      const commentsSnapshot = await getDocs(commentsQuery);
      const commentsData = commentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setComments(commentsData);
    } catch (error) {
      console.error("Error submitting comment:", error);
      setError('Failed to submit comment');
    } finally {
      setSubmitting(false);
      // Clear error/success after 5 seconds
      setTimeout(() => {
        setError('');
        setSuccess('');
      }, 5000);
    }
  };

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error && !post) {
    return (
      <div className="container py-5 text-center">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
        <Link to="/blog" className="btn btn-primary">Back to Blog</Link>
      </div>
    );
  }

  return (
    <main className="main">

      <Header />

      {/* Page Title */}
      <div className="page-title dark-background" 
          data-aos="fade" 
          style={{ backgroundImage: "url(/assets/img/page-title-bg.webp)" }}>
        <div className="container position-relative">
          <h1>Blog Details</h1>
          <p>{post?.summary || 'Read our latest blog post'}</p>
          <nav className="breadcrumbs">
            <ol>
              <li><a href="/">Home</a></li>
              <li><Link to="/blog">Blog</Link></li>
              <li className="current">{post?.title}</li>
            </ol>
          </nav>
        </div>
      </div>

      <div className="container">
        <div className="row">
          <div className="col-lg-8">
            {/* Blog Details Section */}
            <section id="blog-details" className="blog-details section">
              <div className="container">
                <article className="article">
                  <div className="post-img">
                    <img src={post?.imageUrl || "/assets/img/blog/Blog1.png"} alt="" className="img-fluid" />
                  </div>

                  <h2 className="title">{post?.title}</h2>

                  <div className="meta-top">
                    <ul>
                      <li className="d-flex align-items-center"><i className="bi bi-person"></i> <a href=" ">{post?.author}</a></li>
                      <li className="d-flex align-items-center"><i className="bi bi-clock"></i> <a href=" "><time dateTime={post?.createdAt?.toDate()?.toISOString()}>
                        {post?.createdAt ? new Date(post.createdAt.toDate()).toLocaleDateString() : 'Unknown date'}
                      </time></a></li>
                      <li className="d-flex align-items-center"><i className="bi bi-chat-dots"></i> <a href="#blog-comments">{comments.length} Comments</a></li>
                    </ul>
                  </div>

                  <div className="content" dangerouslySetInnerHTML={{ __html: post?.content }}></div>

                  <div className="meta-bottom">
                    <i className="bi bi-folder"></i>
                    <ul className="cats">
                      <li><a href=" ">{post?.category}</a></li>
                    </ul>

                    {post?.tags && post.tags.length > 0 && (
                      <>
                        <i className="bi bi-tags"></i>
                        <ul className="tags">
                          {post.tags.map((tag, index) => (
                            <li key={index}><a href={`/blog?tag=${encodeURIComponent(tag)}`}>{tag}</a></li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                </article>
              </div>
            </section>

            {/* Blog Comments Section */}
            <section id="blog-comments" className="blog-comments section">
              <div className="container">
                <h4 className="comments-count">{comments.length} Comments</h4>

                {comments.map((comment, index) => (
                  <div id={`comment-${index + 1}`} className="comment" key={comment.id}>
                    <div className="d-flex">
                      <div className="comment-img">
                        <img src={`https://ui-avatars.com/api/?name=${encodeURIComponent(comment.name)}&background=random`} alt={comment.name} />
                      </div>
                      <div>
                        <h5>
                          <a href={comment.website || '#'}>{comment.name}</a>
                          <a href="#comment-form" className="reply"><i className="bi bi-reply-fill"></i> Reply</a>
                        </h5>
                        <time dateTime={comment.createdAt?.toDate()?.toISOString()}>
                          {comment.createdAt ? new Date(comment.createdAt.toDate()).toLocaleDateString() : 'Recent'}
                        </time>
                        <p>{comment.comment}</p>
                      </div>
                    </div>
                  </div>
                ))}

                {comments.length === 0 && (
                  <div className="alert alert-info">
                    No comments yet. Be the first to comment!
                  </div>
                )}
              </div>
            </section>

            {/* Comment Form Section */}
            <section id="comment-form" className="comment-form section">
              <div className="container">
                <form onSubmit={handleCommentSubmit}>
                  <h4>Post Comment</h4>
                  <p>Your email address will not be published. Required fields are marked * </p>
                  
                  {error && <div className="alert alert-danger">{error}</div>}
                  {success && <div className="alert alert-success">{success}</div>}
                  
                  <div className="row">
                    <div className="col-md-6 form-group">
                      <input 
                        name="name" 
                        type="text" 
                        className="form-control" 
                        placeholder="Your Name*"
                        value={commentForm.name}
                        onChange={handleCommentChange}
                        required
                      />
                    </div>
                    <div className="col-md-6 form-group">
                      <input 
                        name="email" 
                        type="email" 
                        className="form-control" 
                        placeholder="Your Email*"
                        value={commentForm.email}
                        onChange={handleCommentChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col form-group">
                      <input 
                        name="website" 
                        type="text" 
                        className="form-control" 
                        placeholder="Your Website"
                        value={commentForm.website}
                        onChange={handleCommentChange}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col form-group">
                      <textarea 
                        name="comment" 
                        className="form-control" 
                        placeholder="Your Comment*"
                        value={commentForm.comment}
                        onChange={handleCommentChange}
                        required
                      ></textarea>
                    </div>
                  </div>
                  <div className="text-center">
                    <button 
                      type="submit" 
                      className="btn btn-primary"
                      disabled={submitting}
                    >
                      {submitting ? 'Submitting...' : 'Post Comment'}
                    </button>
                  </div>
                </form>
              </div>
            </section>
          </div>

          <div className="col-lg-4 sidebar">
            <div className="widgets-container">
              {/* Blog Author Widget */}
              {post?.authorBio && (
                <div className="blog-author-widget widget-item">
                  <div className="d-flex flex-column align-items-center">
                    <div className="author-img">
                        <h4>{post.author}</h4>
                        <div className="social-links">
                          {post.authorSocial?.twitter && <a href={post.authorSocial.twitter}><i className="bi bi-twitter-x"></i></a>}
                          {post.authorSocial?.facebook && <a href={post.authorSocial.facebook}><i className="bi bi-facebook"></i></a>}
                          {post.authorSocial?.instagram && <a href={post.authorSocial.instagram}><i className="bi bi-instagram"></i></a>}
                          {post.authorSocial?.linkedin && <a href={post.authorSocial.linkedin}><i className="bi bi-linkedin"></i></a>}
                        </div>
                      </div>
                  </div>
                  <p>{post.authorBio}</p>
                </div>
              )}
              

              {/* Search Widget */}
              <div className="search-widget widget-item">
                <h3 className="widget-title">Search</h3>
                <form action="/blog">
                  <input type="text" name="search" />
                  <button type="submit" title="Search"><i className="bi bi-search"></i></button>
                </form>
              </div>

              {/* Categories Widget */}
              <CategoriesWidget />

              {/* Recent Posts Widget */}
              <div className="recent-posts-widget-2 widget-item">
                <h3 className="widget-title">Recent Posts</h3>
                {relatedPosts.map(relatedPost => (
                  <div className="post-item" key={relatedPost.id}>
                    <h4><Link to={`/blog/${relatedPost.id}`}>{relatedPost.title}</Link></h4>
                    <time dateTime={relatedPost.createdAt?.toDate()?.toISOString()}>
                      {relatedPost.createdAt ? new Date(relatedPost.createdAt.toDate()).toLocaleDateString() : 'Recent'}
                    </time>
                  </div>
                ))}
                {relatedPosts.length === 0 && (
                  <p>No related posts found.</p>
                )}
              </div>

              {/* Tags Widget */}
              <div className="tags-widget widget-item">
                <h3 className="widget-title">Tags</h3>
                <ul>
                  {(() => {
                    // Default tags list
                    const defaultTags = ["Farming", "Chia", "Macadamia", "Contract Farming", "Moringa", "Flax", "Quinoa", "Avocados", "Mushrooms", "Tips", "Marketing"];
                    
                    // Combine default tags with post tags (if any)
                    let combinedTags = [...defaultTags];
                    
                    // Add post tags if they don't already exist in the default list
                    if (post?.tags && post.tags.length > 0) {
                      post.tags.forEach(tag => {
                        if (!combinedTags.includes(tag)) {
                          combinedTags.push(tag);
                        }
                      });
                    }
                    
                    // Return the combined tags list
                    return combinedTags.map(tag => (
                      <li key={tag}>
                        <a href={`/blog?tag=${encodeURIComponent(tag)}`}>
                          {tag}
                          {/* Highlight if tag is in current post */}
                          {post?.tags && post.tags.includes(tag) && <span className="badge bg-primary ms-1" style={{ fontSize: '0.7em' }}>•</span>}
                        </a>
                      </li>
                    ));
                  })()}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default BlogDetail;